import {useTranslation} from 'react-i18next'
import {useState} from 'react'
import {errorHandler, openLinkInNewTab} from '../../../helpers/utils'
import {PageHelmet} from '../../../components/page-helmet/PageHelmet'
import {httpGetUserImpersonate} from '../../../http-requests/admin/users.http'
import {debounce} from 'lodash-es'
import {Table} from '../../../components/table/Table'
import {PartnersWrapper, StyledHeadingPartner, ImpersonationOverlay, StyledReasonText} from './style'
import {UserIcon} from '../../../assets/icons/UserIcon'
import {EmailIcon} from '../../../assets/icons/EmailIcon'
import {HashtagIcon} from '../../../assets/icons/HashtagIcon'
import {ClockIcon} from '../../../assets/icons/ClockIcon'
import {CircleIcon} from '../../../assets/icons/CircleIcon'
import {usePartners} from '../../../hooks/usePartners'
import {EditModal} from './components/edit-freelancer-modal/EditFreelancerModal'
import {InputText} from '../../../components/input-text/InputText'
import {useUserStore} from '../../../features/user/store'
import {SearchLgIcon} from '../../../components/icon'
import {useTheme} from 'styled-components'
import {InputHelpText} from '../../../components/input-help-text/InputHelpText'
import {Flexbox} from '../../../components/flexbox/Flexbox'
import {useImpersonateNavigateUser} from '../../../hooks/useImpersonateNavigateUser'
import {Spinner} from '../../../components/spinner/Spinner'
import {FreelancerActionsDropdown} from './components/freelancer-actions-dropdown/FreelancerActionsDropdown'
import {Badge} from '../../../components/badge/Badge'
import {FreelancerStatus} from '../../../features/admin/types'

export const AdminFreelancers = () => {
    const {t} = useTranslation()
    const theme = useTheme()
    const setUser = useUserStore(store => store.setUser)
    const adminAccessToken = useUserStore(store => store.adminAccessToken)
    const [searchError, setSearchError] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [editDefaultData, setEditDefaultData] = useState({airbnbId: '', email: '', id: ''})
    const query = usePartners()
    // Hook for secure Navigate impersonation with postMessage
    const {isLoading: isImpersonating, impersonateNavigateUser} = useImpersonateNavigateUser(adminAccessToken)

    const freelancerStatuses = {
        [FreelancerStatus.Enum.active_available]: {
            badgeVariant: 'success',
            badgeLabel: t('admin:freelancers_deactivation:status:active')
        },
        [FreelancerStatus.Enum.active_unavailable]: {
            badgeVariant: 'success',
            badgeLabel: t('admin:freelancers_deactivation:status:active')
        },
        [FreelancerStatus.Enum.suspended]: {
            badgeVariant: 'warning',
            badgeLabel: t('admin:freelancers_deactivation:status:suspended')
        },
        [FreelancerStatus.Enum.deactivated]: {
            badgeVariant: 'danger',
            badgeLabel: t('admin:freelancers_deactivation:status:deactivated')
        }
    }
    const tableColumns = [
        {
            label: (
                <Flexbox align="center" gap={1}>
                    <HashtagIcon width={16} /> {t('commons:airbnb_id')}
                </Flexbox>
            ),
            width: '15%',
            cellRender: freelancer => <span>{freelancer.airbnb_user_id || '-'}</span>
        },
        {
            sortName: 'name',
            label: (
                <Flexbox align="center" gap={1}>
                    <UserIcon size={16} /> {t('commons:name')}
                </Flexbox>
            ),
            width: '25%',
            cellRender: freelancer => <span>{freelancer.name}</span>
        },
        {
            sortName: 'email',
            label: (
                <Flexbox align="center" gap={1}>
                    <EmailIcon size={16} /> {t('commons:email')}
                </Flexbox>
            ),
            width: '20%',
            cellRender: freelancer => <span>{freelancer.email}</span>
        },
        {
            sortName: 'in_progress_assignments_count',
            label: (
                <Flexbox align="center" gap={1} style={{whiteSpace: 'wrap'}}>
                    <ClockIcon size={16} /> {t('admin:assignments_in_progress')}
                </Flexbox>
            ),
            width: '15%',
            cellRender: freelancer => <span>{freelancer.in_progress_assignments_count}</span>
        },
        {
            label: (
                <Flexbox align="center" gap={1}>
                    <CircleIcon /> {t('commons:status')}
                </Flexbox>
            ),
            width: '20%',
            cellRender: freelancer => (
                <Flexbox direction="column" gap={1}>
                    <Badge variant={freelancerStatuses[freelancer.status].badgeVariant}>
                        {freelancerStatuses[freelancer.status].badgeLabel}
                    </Badge>
                    {freelancer.status == FreelancerStatus.Enum.deactivated && (
                        <StyledReasonText title={freelancer.deactivation_reason}>
                            {freelancer.deactivation_reason}
                        </StyledReasonText>
                    )}
                    {freelancer.status == FreelancerStatus.Enum.suspended && (
                        <StyledReasonText title={freelancer.suspension_reason}>
                            {freelancer.suspension_reason}
                        </StyledReasonText>
                    )}
                </Flexbox>
            )
        },
        {
            width: '5%',
            alignment: 'right',
            cellRender: freelancer => (
                <FreelancerActionsDropdown
                    freelancer={freelancer}
                    onImpersonate={onImpersonate}
                    onImpersonateNavigate={impersonateNavigateUser}
                    onEdit={onEditClick}
                />
            )
        }
    ]

    const onSubmit = debounce(e => {
        const value = e?.target?.value
        if (value?.length >= 3) {
            query.setSearch(value)
            setSearchError(null)
        } else if (!value?.length) {
            query.setSearch(null)
            setSearchError(null)
        } else {
            query.setSearch(null)
            setSearchError({message: t('errors:search_invalid_length')})
        }
    }, 750)

    const onImpersonate = async userId => {
        try {
            setIsLoading(true)
            const {data} = await httpGetUserImpersonate(adminAccessToken, userId)
            setUser({user: {}, access_token: data, refresh_token: ''})
            openLinkInNewTab(`${window.location.origin}`)
        } catch (e) {
            errorHandler(e)
        } finally {
            setIsLoading(false)
        }
    }

    const onEditClick = item => {
        setShowModal(true)
        setEditDefaultData({airbnbId: item.airbnb_user_id, email: item.email, id: item.id})
    }

    return (
        <PartnersWrapper>
            <PageHelmet title={t('titles:admin_partners')} />
            <StyledHeadingPartner>
                <h1>{t('admin:partners')}</h1>
                <InputText
                    type="text"
                    typeIcon={<SearchLgIcon size={20} fill={theme.palette.neutral['400']} />}
                    inputSize="sm"
                    width="280px"
                    placeholder={t('admin:search_by_name_email')}
                    onChange={onSubmit}
                    autoComplete="off"
                />
                {!!searchError?.message && <InputHelpText style={{position: 'absolute'}} error={searchError.message} />}
            </StyledHeadingPartner>
            <Table
                columns={tableColumns}
                data={query.remappedData}
                isError={query.isError}
                isChangingPage={query.isFetchingNextPage}
                isLoading={query.isLoading}
                onChangePage={query.onChangePage}
                searchValue={query.search}
                sorter={{
                    orderBy: query.sorter.order_by,
                    orderDirection: query.sorter.order_direction,
                    onSort: ({orderBy, orderDirection}) => query.onSortColumn(orderBy, orderDirection)
                }}
            />
            {(isLoading || isImpersonating) && (
                <ImpersonationOverlay>
                    <div>
                        <Spinner size={24} />
                        <h2>{t('admin:impersonating_in_progress')}</h2>
                    </div>
                </ImpersonationOverlay>
            )}
            {showModal && <EditModal defaultValues={editDefaultData} onClose={() => setShowModal(false)} />}
        </PartnersWrapper>
    )
}

AdminFreelancers.displayName = 'AdminFreelancers'
